import {
  DictationSentenceStatusEnum,
  DictationWordStatusEnum,
  DictationWordTypeEnum,
} from '@doltech/utils/lib/constants';
import { isNil } from 'lodash';
import { flow, getParent, Instance, types } from 'mobx-state-tree';

import { DoDictationWord, DoDictationWordInstance } from './do-dictation-word.store';
import { DictationSentence } from './sentence.store';
import { simpleSplitSentenceToWordsWithoutPunctuation } from '@doltech/utils/lib/sentence';
import { DUAL_TOGGLE } from '@doltech/domains/dictation/shared/constant';
import { SentenceLearnHistory } from './sentence-learn-history.store';
import { convertCurlyQuotationMark } from '@doltech/utils/lib/text';
import { compareWord } from '@doltech/utils/lib/word-checker/word-checker.api';

export const DoDictationSentence = DictationSentence.named('DoDictationSentence')
  .props({
    words: types.array(DoDictationWord),
    status: types.enumeration<DictationSentenceStatusEnum>(
      'SentenceStatus',
      Object.values(DictationSentenceStatusEnum)
    ),
    isLearnedShadowingPronun: types.maybeNull(types.optional(types.boolean, false)),
    learnedHistory: types.maybeNull(SentenceLearnHistory),
    audioTimeInSecond: types.optional(types.number, 0),
    character: types.maybeNull(types.string),
    content: types.string,
    checked: false,
    checkCount: 0,
    dualToggle: types.maybeNull(types.string),
    freeTextValue: types.maybeNull(types.string),
    isFreeTextIncorrect: types.maybeNull(types.optional(types.boolean, false)),
  })
  .actions((self) => {
    const words = self.words as DoDictationWordInstance[];

    const setChecked = (value: boolean) => {
      self.checked = value;
    };

    const setUntouched = () => {
      self.status = DictationSentenceStatusEnum.UNTOUCHED;
      self.checkCount = 0;
    };

    return {
      setChecked,
      setUntouched,
      setCheckCount(value: number) {
        self.checkCount = value;
      },
      reset() {
        if (self.dualToggle === DUAL_TOGGLE.SUGGEST_TEXT) {
          words.forEach((word) => {
            word.setUntouched();
          });
          setUntouched();
          self.checked = false;
        } else {
          self.checked = false;
          self.isFreeTextIncorrect = false;
          if (self.status !== DictationSentenceStatusEnum.IN_PROGRESS) {
            self.status = DictationSentenceStatusEnum.IN_PROGRESS;
          }
        }
      },
      reOrder() {
        let wrongPositionWords = words.filter((word) => word.isWrongPosition);
        const cached = words.map(({ id, value, studentValue }) => ({ id, value, studentValue }));

        while (wrongPositionWords.length > 0) {
          const wrongPositionWord = wrongPositionWords[0];

          const correctIndex = words.findIndex(
            (blankWord) =>
              wrongPositionWord.studentValue.toLowerCase() === blankWord.value.toLowerCase() &&
              blankWord.isBlank &&
              blankWord.id !== wrongPositionWord.id
          );

          if (correctIndex > -1) {
            const correctWord = words[correctIndex];
            correctWord.setStudentValue(correctWord.value);
            correctWord.setCorrect();

            const hasSwapCase = cached.find(
              (c) => c.studentValue?.toLowerCase() === wrongPositionWord.value.toLowerCase()
            );

            if (hasSwapCase) {
              wrongPositionWord.setStudentValue(wrongPositionWord.value);
              wrongPositionWord.setCorrect();
            } else {
              wrongPositionWord.setStudentValue('');
              wrongPositionWord.setUntouched();
            }
          } else {
            return;
          }

          wrongPositionWords = words.filter((word) => word.isWrongPosition);
        }
      },
      check: flow(function* checkSentence(isSelfClick = false) {
        const isAllPunctuation =
          words.length &&
          words.every((eachWord) => eachWord.type === DictationWordTypeEnum.PUNCTUATION);
        if (isAllPunctuation) {
          setChecked(true);
          self.status = DictationSentenceStatusEnum.CORRECT;
          return;
        }
        const isSentenceVirgin = words.every(
          (word) => isNil(word.studentValue) || word.studentValue === ''
        );
        if (isSentenceVirgin) {
          if (isSelfClick) {
            self.checked = true;
          }
          return;
        }

        let correctCount = 0;
        // 1. check word -> compare word.studentValue & word.value as same index (BLANK only)
        // 2. check position: check word.studentValue with other word (BLANK only)
        const blankWords = words.filter((word) => word.type === DictationWordTypeEnum.BLANK);
        const allPromises = blankWords.map(async (currentWord) => {
          await currentWord.check();
          if (currentWord.isCorrect) {
            correctCount += 1;
          }

          const otherWords = blankWords.filter((otherWord) => otherWord.id !== currentWord.id);
          otherWords.forEach((otherWord) => {
            if (otherWord.value.toLowerCase() === currentWord.studentValue?.toLowerCase()) {
              currentWord.setWrongPosition();
            }
          });
        });

        yield Promise.all(allPromises);

        setChecked(true);
        self.checkCount += 1;

        if (correctCount === blankWords.length) {
          self.status = DictationSentenceStatusEnum.CORRECT;
        } else {
          self.status = DictationSentenceStatusEnum.IN_PROGRESS;
        }
      }),
      freeTextCheck: flow(function* checkFreeText() {
        const isAllPunctuation =
          words.length &&
          words.every((eachWord) => eachWord.type === DictationWordTypeEnum.PUNCTUATION);
        if (isAllPunctuation) {
          setChecked(true);
          self.status = DictationSentenceStatusEnum.CORRECT;
          return;
        }
        const isSentenceVirgin = isNil(self.freeTextValue) || self.freeTextValue === '';
        if (isSentenceVirgin) return;
        setChecked(true);
        self.checkCount += 1;
        const contentWithoutPunctuation = self.words
          .filter((x) => x?.type !== DictationWordTypeEnum.PUNCTUATION)
          .map((x) => x?.value);

        const freeTextValueWithoutPunctuation =
          self.freeTextValue &&
          simpleSplitSentenceToWordsWithoutPunctuation(convertCurlyQuotationMark(self.freeTextValue));

        if (
          !freeTextValueWithoutPunctuation?.length ||
          freeTextValueWithoutPunctuation?.length !== contentWithoutPunctuation.length
        ) {
          self.status = DictationSentenceStatusEnum.IN_PROGRESS;
          self.isFreeTextIncorrect = true;
          return;
        }

        const allPromises = freeTextValueWithoutPunctuation.map(async (word, idx) =>
          compareWord({
            word1: word,
            word2: contentWithoutPunctuation[idx],
          })
        );
        const allPromisesValue = yield Promise.all(allPromises);
        const isCorrect = allPromisesValue.every((x) => x === true);
        if (isCorrect) {
          self.status = DictationSentenceStatusEnum.CORRECT;
        } else {
          self.status = DictationSentenceStatusEnum.IN_PROGRESS;
          self.isFreeTextIncorrect = true;
        }
      }),
      setCorrect() {
        self.status = DictationSentenceStatusEnum.CORRECT;
      },
      setInProgress() {
        self.status = DictationSentenceStatusEnum.IN_PROGRESS;
      },
      setLearnedShadowingPronun(isLearnedShadowingPronun) {
        self.isLearnedShadowingPronun = isLearnedShadowingPronun;
      },
      setLearnedHistory(learnedHistory) {
        self.learnedHistory = learnedHistory;
      },
      setDualToggle(dualToggle) {
        self.dualToggle = dualToggle;
      },
      setFreeTextValue(freeTextValue) {
        self.freeTextValue = freeTextValue;
      },
      setIsFreeTextIncorrect(value) {
        self.isFreeTextIncorrect = value;
      },
    };
  })
  .views((self) => {
    const words = self.words as DoDictationWordInstance[];
    const hasBlank = () => words.some((word) => word.type === DictationWordTypeEnum.BLANK);

    return {
      getWordById(id: string): DoDictationWordInstance {
        return words.find((word) => word.id === id);
      },
      get isUntouched() {
        if (
          self.dualToggle === DUAL_TOGGLE.SUGGEST_TEXT &&
          self.status !== DictationWordStatusEnum.CORRECT
        ) {
          const isUntouched = words
            .map((word) => word.status)
            .every((status) => status === DictationWordStatusEnum.UNTOUCHED);

          if (isUntouched && hasBlank()) {
            self.setUntouched();
          }

          return isUntouched;
        }
        return false;
      },
      get isCorrect() {
        return self.status === DictationSentenceStatusEnum.CORRECT;
      },
      get isInProgress() {
        return self.status === DictationSentenceStatusEnum.IN_PROGRESS;
      },
      get isDirty() {
        if (self.dualToggle === DUAL_TOGGLE.SUGGEST_TEXT) {
          const isDirty = words.map((word) => word.isUntouched).includes(false);

          if (isDirty) {
            self.setInProgress();
          }

          return isDirty;
        }
        return false;
      },

      get hasNoBlank() {
        return !hasBlank();
      },

      get isWordsCorrect() {
        const wordsWithoutPunctuation = words.filter(
          (x) => x?.type !== DictationWordTypeEnum.PUNCTUATION
        );
        return (
          wordsWithoutPunctuation.length &&
          wordsWithoutPunctuation.every((word) => word.status === DictationWordStatusEnum.CORRECT)
        );
      },
      get hasIncorrectWord() {
        if (self.dualToggle === DUAL_TOGGLE.SUGGEST_TEXT) {
          return words.some((word) => word.isIncorrect);
        }
        return self.isFreeTextIncorrect;
      },

      get hasWrongOrderWords() {
        if (self.dualToggle === DUAL_TOGGLE.SUGGEST_TEXT) {
          return words.some((word) => word.isWrongPosition);
        }
        return false;
      },
      get isLastSentence() {
        const sentences = getParent(self) as any[];
        return sentences[sentences.length - 1] === self;
      },
      get blankWordsCount() {
        return words.filter((w) => w.type === DictationWordTypeEnum.BLANK).length;
      },
      get canCheck() {
        if (self.dualToggle === DUAL_TOGGLE.SUGGEST_TEXT) {
          return true;
          // return words.some((w) => w.status === DictationWordStatusEnum.DIRTY);
        }
        return self.freeTextValue;
      },
    };
  });

export interface DoDictationSentenceInstance extends Instance<typeof DoDictationSentence> {}
